/**
 * File: MarkdownText.tsx
 * Description: Markdown text renderer for assistant-ui
 * Author: System
 * Created: 2025-09-03
 * Last Modified: 2025-09-03
 */

import React from 'react';

import { cn } from '../../shared/utils';
import {
  closesFence,
  MERMAID_LANGUAGE,
  normalizeLineEndings,
  parseFenceLine,
} from '../../utils/markdownFences';
import { MermaidRenderer } from '../markdown/MermaidRenderer';

interface MarkdownTextProps {
  content: string;
  className?: string;
}

export const MarkdownText: React.FC<MarkdownTextProps> = ({ content, className }) => {
  // Process inline markdown formatting like **bold**, *italic*, `code`, [links](url)
  const processInlineFormatting = (text: string, baseKey: string): JSX.Element[] => {
    const elements: JSX.Element[] = [];
    let elementKey = 0;

    const getNextKey = () => {
      const key = elementKey;
      elementKey += 1;
      return key;
    };

    // Process links first
    const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
    let linkMatch;
    const linkMatches: Array<{ start: number; end: number; element: JSX.Element }> = [];
    
    while ((linkMatch = linkRegex.exec(text)) !== null) {
      const linkKey = getNextKey();
      linkMatches.push({
        start: linkMatch.index,
        end: linkMatch.index + linkMatch[0].length,
        element: (
          <a key={`${baseKey}-link-${linkKey}`} href={linkMatch[2]} className='text-primary font-medium underline underline-offset-4 break-all' target='_blank' rel='noopener noreferrer'>
            {linkMatch[1]}
          </a>
        ),
      });
    }

    // Helper functions for pattern components
    const createStrong = (match: string, key: number) => (
      <strong key={`${baseKey}-strong-${key}`}>{match}</strong>
    );

    const createEm = (match: string, key: number) => (
      <em key={`${baseKey}-em-${key}`}>{match}</em>
    );

    const createCode = (match: string, key: number) => (
      <code key={`${baseKey}-code-${key}`} className='bg-muted rounded border font-semibold px-1 py-0.5 text-xs'>{match}</code>
    );

    // Process other inline formats
    const otherPatterns = [
      { regex: /\*\*(.*?)\*\*/g, component: createStrong },
      { regex: /\*(.*?)\*/g, component: createEm },
      { regex: /`(.*?)`/g, component: createCode },
    ];

    const otherMatches: Array<{ start: number; end: number; element: JSX.Element }> = [];

    otherPatterns.forEach(pattern => {
      const regex = new RegExp(pattern.regex.source, 'g');
      let match;
      while ((match = regex.exec(text)) !== null) {
        const matchKey = getNextKey();
        otherMatches.push({
          start: match.index,
          end: match.index + match[0].length,
          element: pattern.component(match[1], matchKey),
        });
      }
    });

    // Combine all matches
    const allMatches = [...linkMatches, ...otherMatches];

    // Sort matches by start position
    allMatches.sort((a, b) => a.start - b.start);

    // Remove overlapping matches (keep the first one)
    const validMatches = [];
    let lastEnd = 0;
    for (const match of allMatches) {
      if (match.start >= lastEnd) {
        validMatches.push(match);
        lastEnd = match.end;
      }
    }

    // Build the final elements array
    let textIndex = 0;
    validMatches.forEach(match => {
      // Add text before the match
      if (match.start > textIndex) {
        const beforeText = text.substring(textIndex, match.start);
        if (beforeText) {
          const textKey = getNextKey();
          elements.push(<span key={`${baseKey}-text-${textKey}`}>{beforeText}</span>);
        }
      }
      // Add the formatted element
      elements.push(match.element);
      textIndex = match.end;
    });

    // Add remaining text
    if (textIndex < text.length) {
      const remainingText = text.substring(textIndex);
      if (remainingText) {
        const textKey = getNextKey();
        elements.push(<span key={`${baseKey}-text-${textKey}`}>{remainingText}</span>);
      }
    }

    return elements.length > 0 ? elements : [<span key={`${baseKey}-text-0`}>{text}</span>];
  };

  const renderTable = (rows: string[], startKey: number): JSX.Element => {
    if (rows.length === 0) {return <></>;}
    
    // Split each row into columns
    const tableData = rows.map(row => 
      row.split('|').map(cell => cell.trim()).filter(cell => cell !== ''),
    );
    
    // Find separator row (contains only dashes and spaces)
    let headerRowCount = 1;
    for (let i = 1; i < tableData.length; i++) {
      const row = tableData[i];
      if (row.every(cell => /^[\s\-:]+$/.test(cell))) {
        break;
      }
      headerRowCount = i + 1;
    }
    
    const headerRows = tableData.slice(0, headerRowCount);
    const bodyRows = tableData.slice(headerRowCount + 1); // Skip separator row
    
    return (
      <div className="my-4 overflow-x-auto">
        <table className="min-w-full text-sm border-collapse border border-base-300">
          {headerRows.length > 0 && (
            <thead className="bg-base-200">
              {headerRows.map((row, rowIndex) => (
                <tr key={`header-${rowIndex}`}>
                  {row.map((cell, cellIndex) => (
                    <th 
                      key={`header-${rowIndex}-${cellIndex}`} 
                      className="border border-base-300 px-3 py-2 text-left font-semibold"
                    >
                      {processInlineFormatting(cell, `header-${rowIndex}-${cellIndex}`)}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
          )}
          <tbody>
            {bodyRows.map((row, rowIndex) => (
              <tr key={`body-${rowIndex}`} className="even:bg-base-100 odd:bg-base-50">
                {row.map((cell, cellIndex) => (
                  <td 
                    key={`body-${rowIndex}-${cellIndex}`} 
                    className="border border-base-300 px-3 py-2"
                  >
                    {processInlineFormatting(cell, `body-${rowIndex}-${cellIndex}`)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderMarkdown = (content: string) => {
    const lines = normalizeLineEndings(content).split('\n');
    const elements: JSX.Element[] = [];
    let currentCodeBlock = '';
    let isInCodeBlock = false;
    let codeLanguage = '';
    let openFenceMarker = '';
    let tableRows: string[] = [];
    let isInTable = false;

    // Renders the buffered code block. Also called at the end of the content
    // so a fence the generator left unterminated still shows its diagram
    // instead of silently disappearing.
    const flushCodeBlock = (key: number): void => {
      if (!isInCodeBlock) { return; }
      if (codeLanguage === MERMAID_LANGUAGE) {
        elements.push(
          <div key={key} className='my-4'>
            <MermaidRenderer code={currentCodeBlock} />
          </div>,
        );
      } else {
        elements.push(
          <pre key={key} className={cn('overflow-x-auto rounded-b-lg bg-black p-4 text-white my-4')}>
            <code className={`language-${codeLanguage} text-sm`}>{currentCodeBlock}</code>
          </pre>,
        );
      }
      currentCodeBlock = '';
      isInCodeBlock = false;
      codeLanguage = '';
      openFenceMarker = '';
    };

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const fence = parseFenceLine(line);

      if (isInCodeBlock) {
        if (fence && closesFence(fence, openFenceMarker)) {
          flushCodeBlock(i);
        } else {
          currentCodeBlock += `${line}\n`;
        }
      } else if (fence) {
        codeLanguage = fence.language;
        openFenceMarker = fence.marker;
        isInCodeBlock = true;
      } else {
        // Check for table rows (contains pipe characters)
        if (line.includes('|') && line.trim() !== '') {
          if (!isInTable) {
            isInTable = true;
            tableRows = [];
          }
          tableRows.push(line);
        } else {
          // If we were in a table, render it now
          if (isInTable) {
            elements.push(
              <React.Fragment key={`table-${i}`}>
                {renderTable(tableRows, i)}
              </React.Fragment>,
            );
            tableRows = [];
            isInTable = false;
          }
          
          // Regular markdown content
          if (line.startsWith('# ')) {
            elements.push(
              <h1 key={i} className={cn('mb-3 mt-4 scroll-m-20 text-2xl font-bold tracking-tight first:mt-0 last:mb-0')}>
                {processInlineFormatting(line.substring(2), `h1-${i}`)}
              </h1>,
            );
          } else if (line.startsWith('## ')) {
            elements.push(
              <h2 key={i} className={cn('mb-3 mt-4 scroll-m-20 text-xl font-semibold tracking-tight first:mt-0 last:mb-0')}>
                {processInlineFormatting(line.substring(3), `h2-${i}`)}
              </h2>,
            );
          } else if (line.startsWith('### ')) {
            elements.push(
              <h3 key={i} className={cn('mb-2 mt-4 scroll-m-20 text-lg font-semibold tracking-tight first:mt-0 last:mb-0')}>
                {processInlineFormatting(line.substring(4), `h3-${i}`)}
              </h3>,
            );
          } else if (line.startsWith('#### ')) {
            elements.push(
              <h4 key={i} className={cn('mb-2 mt-3 scroll-m-20 text-base font-semibold tracking-tight first:mt-0 last:mb-0')}>
                {processInlineFormatting(line.substring(5), `h4-${i}`)}
              </h4>,
            );
          } else if (line.startsWith('##### ')) {
            elements.push(
              <h5 key={i} className={cn('mb-2 mt-3 scroll-m-20 text-sm font-medium tracking-tight first:mt-0 last:mb-0')}>
                {processInlineFormatting(line.substring(6), `h5-${i}`)}
              </h5>,
            );
          } else if (line.startsWith('###### ')) {
            elements.push(
              <h6 key={i} className={cn('mb-2 mt-3 scroll-m-20 text-xs font-medium tracking-tight first:mt-0 last:mb-0')}>
                {processInlineFormatting(line.substring(7), `h6-${i}`)}
              </h6>,
            );
          } else if (line.startsWith('- ') || line.startsWith('* ')) {
            elements.push(
              <li key={i} className={cn('my-1 ml-5 text-sm')}>
                {processInlineFormatting(line.substring(2), `li-${i}`)}
              </li>,
            );
          } else if (line.trim() === '---' || line.trim() === '***' || line.trim() === '___') {
            elements.push(
              <hr key={i} className={cn('my-4 border-t border-base-300')} />,
            );
          } else if (line.trim() === '') {
            elements.push(<br key={i} />);
          } else {
            elements.push(
              <p key={i} className={cn('mb-3 mt-3 leading-6 text-sm first:mt-0 last:mb-0')}>
                {processInlineFormatting(line, `p-${i}`)}
              </p>,
            );
          }
        }
      }
    }

    // Handle remaining code block / table at end of content
    flushCodeBlock(lines.length + 1);
    if (isInTable && tableRows.length > 0) {
      elements.push(
        <React.Fragment key={`table-${lines.length}`}>
          {renderTable(tableRows, lines.length)}
        </React.Fragment>,
      );
    }

    return elements;
  };

  return (
    <div
      className={cn(
        'prose prose-sm max-w-none break-words [overflow-wrap:anywhere]',
        className,
      )}
    >
      {renderMarkdown(content)}
    </div>
  );
};
